import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export type PdfDocType = 'grant' | 'will' | 'notification';

export interface PdfPreviewData {
  probateId: number;
  deceasedName?: string;
  docType: PdfDocType;
  clientId: number;
  token: string;
  serviceTypeName?: string;
}

@Component({
  selector: 'app-pdf-preview-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './pdf-preview-dialog.component.html',
  styleUrls: ['./pdf-preview-dialog.component.css']
})
export class PdfPreviewDialogComponent implements OnInit, OnDestroy {
  loading = true;
  errorMessage: string | null = null;
  pdfUrl: SafeResourceUrl | null = null;
  private blobUrl: string | null = null;
  private blob: Blob | null = null;

  readonly docTypeMeta: Record<PdfDocType, { label: string; icon: string; theme: string }> = {
    grant: { label: 'Grant of Probate', icon: 'gavel', theme: 'gop' },
    will: { label: 'Will', icon: 'auto_stories', theme: 'will' },
    notification: { label: 'Notification', icon: 'mail', theme: 'notif' }
  };

  constructor(
    public dialogRef: MatDialogRef<PdfPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PdfPreviewData,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.fetchPdf();
  }

  ngOnDestroy(): void {
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
  }

  get meta() { return this.docTypeMeta[this.data.docType]; }

  get downloadFilename(): string {
    if (this.data.docType === 'notification' && this.data.serviceTypeName) {
      return `${this.data.probateId}_${this.data.serviceTypeName}_notification.pdf`;
    }
    const slug = this.data.docType === 'grant'
      ? 'grant_of_probate'
      : this.data.docType;
    return `${this.data.probateId}_${slug}.pdf`;
  }

  private fetchPdf(): void {
    let params = new HttpParams()
      .set('clientId', this.data.clientId)
      .set('token', this.data.token)
      .set('probateId', this.data.probateId)
      .set('docType', this.data.docType);

    if (this.data.docType === 'notification' && this.data.serviceTypeName) {
      params = params.set('serviceTypeName', this.data.serviceTypeName);
    }

    this.http.get('/charitycentral/GetClientPortalDocument', {
      params,
      responseType: 'blob'
    }).subscribe({
      next: (blob: Blob) => {
        this.blob = blob;
        this.blobUrl = URL.createObjectURL(blob);
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
        this.loading = false;
      },
      error: async (err) => {
        this.loading = false;
        if (err.status === 404) {
          this.errorMessage = `${this.meta.label} PDF not found for this probate.`;
        } else if (err.status === 403) {
          this.errorMessage = 'Session expired or access denied.';
        } else {
          this.errorMessage = 'Failed to load preview. Please try again.';
        }
      }
    });
  }

  download(): void {
    if (!this.blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(this.blob);
    a.download = this.downloadFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  openInNewTab(): void {
    if (this.blobUrl) window.open(this.blobUrl, '_blank');
  }

  close(): void {
    this.dialogRef.close();
  }
}
